import { StateStorage } from "zustand/middleware";
import Dexie, { type Table } from "dexie";

const DB_NAME = "zustand-store";

interface StateRecord {
  key: string;
  value: string;
}

class StateDatabase extends Dexie {
  state!: Table<StateRecord>;

  constructor() {
    super(DB_NAME);
    this.version(1).stores({
      state: "key",
    });
  }
}

const db = new StateDatabase();

// Handle database initialization and cleanup
db.open().catch((error) => {
  if (error.name === "UpgradeError" || error.name === "DatabaseClosedError") {
    // Delete the old database and recreate it
    return Dexie.delete(DB_NAME).then(() => {
      location.reload();
    });
  }
  throw error;
});

export function saveState<T>(key: string, value: T): Promise<void> {
  return db.state.put({ key, value: JSON.stringify(value) });
}

export function loadState<T>(key: string): Promise<T | undefined> {
  return db.state.get(key).then((record) => {
    if (!record) return undefined;
    return JSON.parse(record.value) as T;
  });
}

// Create custom storage using IndexedDB
export const indexDBStorage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    const data = await loadState<string>(name);
    return data || null;
  },
  setItem: async (name: string, value: string): Promise<void> => {
    await saveState(name, value);
  },
  removeItem: async (name: string): Promise<void> => {
    await db.state.delete(name);
  },
};

export async function deleteStore(): Promise<void> {
  return db.state.clear();
}

export async function isStoreCreated(): Promise<boolean> {
  const count = await db.state.count();
  return count > 0;
}
